import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { MEDICAL_SPECIALIZATIONS } from '../constants/specializations';
import PaymentGateway from './payments/PaymentGateway';
import { getImageUrl } from '../utils/imageUtils';
import '../styles/DoctorSearch.css';

const DoctorSearch = ({ onBack, onAppointmentBooked }) => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialization, setSelectedSpecialization] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [appointmentType, setAppointmentType] = useState('in-person');
  const [showPaymentGateway, setShowPaymentGateway] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Search when debounced search term or specialization changes
  useEffect(() => {
    searchDoctors();
  }, [debouncedSearchTerm, selectedSpecialization]);

  // Fetch user profile for payment
  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await api.get('/patient/profile');
      if (response.data.success) {
        setUserProfile(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  // Initialize Feather icons
  useEffect(() => {
    const initIcons = () => {
      if (window.feather) {
        window.feather.replace();
      }
    };
    
    // Initialize on mount and when content changes
    initIcons();
    const timer = setTimeout(initIcons, 100);
    
    return () => clearTimeout(timer);
  }, [doctors, showAvailabilityModal, showBookingModal]);

  const searchDoctors = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/patient/search-doctors', {
        params: {
          name: debouncedSearchTerm,
          specialization: selectedSpecialization
        }
      });
      if (response.data.success) {
        setDoctors(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error searching doctors');
      console.error('Error searching doctors:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBookAppointment = async (doctor) => {
    setSelectedDoctor(doctor);
    setShowAvailabilityModal(true);
  };

  const handleDateChange = async (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    setLoadingAvailability(true);
    try {
      const response = await api.get(`/patient/doctor-availability/${selectedDoctor._id}`, {
        params: { date }
      });
      if (response.data.success) {
        setAvailability(response.data.data);
        setError(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching doctor availability');
      console.error('Error fetching doctor availability:', err);
    } finally {
      setLoadingAvailability(false);
    }
  };

  const isSlotInPast = (slot, date) => {
    if (!date || !slot) return false;
    
    const today = new Date();
    const appointmentDate = new Date(date);
    appointmentDate.setHours(0, 0, 0, 0);
    const todayDate = new Date(today);
    todayDate.setHours(0, 0, 0, 0);
    
    // If the date is in the past, the slot is invalid
    if (appointmentDate < todayDate) {
      return true;
    }
    
    // If it's today, check if the slot has started or is ongoing
    if (appointmentDate.getTime() === todayDate.getTime()) {
      const [startHours, startMinutes] = slot.startTime.split(':').map(Number);
      const [endHours, endMinutes] = slot.endTime.split(':').map(Number);
      
      const slotStartTime = new Date(today);
      slotStartTime.setHours(startHours, startMinutes, 0, 0);
      
      const slotEndTime = new Date(today);
      slotEndTime.setHours(endHours, endMinutes, 0, 0);
      
      // Check if the slot is currently ongoing (current time is between start and end)
      if (today >= slotStartTime && today <= slotEndTime) {
        return true; // Slot is currently in progress
      }
      
      // Add 30 minutes buffer - slot must be at least 30 minutes in the future
      const bufferTime = new Date(today.getTime() + 30 * 60000);
      
      // If slot start time is before the buffer time, it's too close or has passed
      if (slotStartTime <= bufferTime) {
        return true;
      }
    }
    
    return false;
  };

  const handleSelectTimeSlot = (slot) => {
    // Validate that the slot is not in the past or ongoing
    if (isSlotInPast(slot, selectedDate)) {
      const today = new Date();
      const appointmentDate = new Date(selectedDate);
      appointmentDate.setHours(0, 0, 0, 0);
      const todayDate = new Date(today);
      todayDate.setHours(0, 0, 0, 0);
      
      // Check if it's an ongoing slot
      if (appointmentDate.getTime() === todayDate.getTime()) {
        const [startHours, startMinutes] = slot.startTime.split(':').map(Number);
        const [endHours, endMinutes] = slot.endTime.split(':').map(Number);
        const slotStartTime = new Date(today);
        slotStartTime.setHours(startHours, startMinutes, 0, 0);
        const slotEndTime = new Date(today);
        slotEndTime.setHours(endHours, endMinutes, 0, 0);
        
        if (today >= slotStartTime && today <= slotEndTime) {
          alert('Cannot select a time slot that is currently in progress. Please select a future time slot.');
        } else {
          alert('Cannot select a time slot that has already passed or is too close to the current time. Please select a future time slot (minimum 30 minutes in advance).');
        }
      } else {
        alert('Cannot select a time slot that has already passed. Please select a future time slot.');
      }
      return;
    }
    
    setSelectedSlot(slot);
    setShowBookingModal(true);
  };

  const handleBookSlot = () => {
    // Validate that the slot is not in the past before proceeding
    if (isSlotInPast(selectedSlot, selectedDate)) {
      alert('Cannot book an appointment in the past. Please select a future time slot.');
      setShowBookingModal(false);
      return;
    }
    
    // Close booking modal and open payment gateway
    setShowBookingModal(false);
    setShowPaymentGateway(true);
  };

  const handlePaymentSuccess = async (paymentData) => {
    console.log('Payment success data received:', paymentData);
    
    try {
      setLoading(true);
      
      // Extract payment ID and transaction ID from response
      // The response structure is: { response: { success: true, data: payment } }
      let paymentId = null;
      
      if (paymentData.response?.data?._id) {
        paymentId = paymentData.response.data._id;
      } else if (paymentData.response?._id) {
        paymentId = paymentData.response._id;
      } else if (paymentData._id) {
        paymentId = paymentData._id;
      }
      
      const transactionId = paymentData.transactionId;
      
      console.log('Full payment data:', JSON.stringify(paymentData, null, 2));
      console.log('Extracted payment ID:', paymentId);
      console.log('Extracted transaction ID:', transactionId);
      
      if (!paymentId || !transactionId) {
        console.error('Missing payment information:', { paymentId, transactionId, paymentData });
        alert(`Payment completed but missing information. Payment ID: ${paymentId}, Transaction: ${transactionId}. Please contact support.`);
        setLoading(false);
        return;
      }
      
      // Book the appointment after successful payment
      const bookingPayload = {
        doctorId: selectedDoctor._id,
        date: selectedDate,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        type: appointmentType,
        paymentId: paymentId,
        transactionId: transactionId
      };
      
      console.log('Booking appointment with payload:', bookingPayload);
      
      const response = await api.post('/patient/book-appointment', bookingPayload);
      
      console.log('Booking response:', response.data);

      if (response.data.success) {
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorSearch.js:176',message:'Appointment booked successfully',data:{appointmentId:response.data.data?._id,hasOnAppointmentBooked:typeof onAppointmentBooked === 'function'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
        // #endregion agent log
        
        // Show success message
        alert('Appointment booked successfully! Payment confirmed.');
        
        // Refresh availability
        const availabilityResponse = await api.get(`/patient/doctor-availability/${selectedDoctor._id}?date=${selectedDate}`);
        if (availabilityResponse.data.success) {
          setAvailability(availabilityResponse.data.data);
        }
        
        // Notify parent component to refresh dashboard data
        if (typeof onAppointmentBooked === 'function') {
          // #region agent log
          fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorSearch.js:191',message:'Calling onAppointmentBooked callback',data:{isAsync:onAppointmentBooked.constructor.name === 'AsyncFunction'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
          // #endregion agent log
          try {
            await onAppointmentBooked();
            // #region agent log
            fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorSearch.js:196',message:'onAppointmentBooked callback completed',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
            // #endregion agent log
          } catch (callbackError) {
            // #region agent log
            fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorSearch.js:200',message:'onAppointmentBooked callback error',data:{errorMessage:callbackError?.message},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
            // #endregion agent log
            console.error('Error in onAppointmentBooked callback:', callbackError);
          }
        }
        
        // Close payment gateway and reset state
        setShowPaymentGateway(false);
        setSelectedSlot(null);
        setSelectedDate('');
        setAppointmentType('in-person');
        setShowAvailabilityModal(false);
      } else {
        console.error('Booking failed:', response.data.message);
        alert('Failed to book appointment: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error booking appointment:', error);
      console.error('Error details:', error.response?.data);
      alert('Error booking appointment: ' + (error.response?.data?.message || error.message || 'Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentCancel = () => {
    setShowPaymentGateway(false);
    setShowBookingModal(true); // Go back to booking modal
  };


  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSpecializationChange = (e) => {
    setSelectedSpecialization(e.target.value);
  };

  const handleCloseModal = () => {
    setShowAvailabilityModal(false);
    setShowBookingModal(false);
    setSelectedDoctor(null);
    setAvailability(null);
    setSelectedDate('');
    setSelectedSlot(null);
    setAppointmentType('in-person');
  };

  const handleBack = () => {
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorSearch.js:223',message:'Back button clicked',data:{iconElementsBefore:document.querySelectorAll('[data-feather]').length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
    // #endregion agent log
    onBack();
    // Re-initialize icons after a delay to ensure PatientDashboard has rendered
    setTimeout(() => {
      if (window.feather && window.feather.replace) {
        window.feather.replace();
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorSearch.js:229',message:'Icons re-initialized after back',data:{iconElementsAfter:document.querySelectorAll('[data-feather]').length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
        // #endregion agent log
      }
    }, 200);
  };

  return (
    <div className="doctor-search">
      {/* Back to Dashboard Button - Top Right Corner */}
      <button onClick={handleBack} className="back-button-top-right">
        <i data-feather="arrow-left"></i>
        <span>Back to Dashboard</span>
      </button>
      
      <div className="search-header">
        <div className="search-header-content">
          <h2>Find a Doctor</h2>
          <p className="search-header-subtitle">Search and book appointments with our expert healthcare providers</p>
        </div>
      </div>

      <div className="search-container">
        <form className="search-form">
          <div className="search-inputs">
            <div className="search-field">
              <label>Doctor Name</label>
              <input
                type="text"
                placeholder="Search doctors by name..."
                value={searchTerm}
                onChange={handleInputChange}
              />
            </div>
            <div className="search-field">
              <label>Specialization</label>
              <select
                value={selectedSpecialization}
                onChange={handleSpecializationChange}
              >
                <option value="">All Specializations</option>
                {MEDICAL_SPECIALIZATIONS.map(spec => (
                  <option key={spec} value={spec}>{spec}</option>
                ))}
              </select>
            </div>
          </div>
        </form>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Searching for doctors...</p>
        </div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="doctors-grid">
          {doctors.map(doctor => (
            <div key={doctor._id} className="doctor-card">
              <div className="doctor-avatar">
                {doctor.profilePicture && getImageUrl(doctor.profilePicture) ? (
                  <img 
                    src={getImageUrl(doctor.profilePicture)} 
                    alt={doctor.name}
                    onError={(e) => {
                      // Fallback to placeholder if image fails to load
                      const placeholder = document.createElement('div');
                      placeholder.className = 'doctor-avatar-placeholder';
                      placeholder.innerHTML = '<i data-feather="user"></i>';
                      e.target.parentElement.replaceChild(placeholder, e.target);
                      if (window.feather) window.feather.replace();
                    }}
                  />
                ) : (
                  <div className="doctor-avatar-placeholder">
                    <i data-feather="user"></i>
                  </div>
                )}
              </div>
              <div className="doctor-info">
                <div className="doctor-info-left">
                  <h3>Dr. {doctor.name}</h3>
                  <p className="specialization">
                    <i data-feather="briefcase"></i>
                    {doctor.doctorDetails.specialization}
                  </p>
                  {doctor.doctorDetails.yearsOfExperience && (
                    <p className="experience">
                      <i data-feather="award"></i>
                      {doctor.doctorDetails.yearsOfExperience} years of experience
                    </p>
                  )}
                  {doctor.doctorDetails.languages && doctor.doctorDetails.languages.length > 0 && (
                    <p className="languages">
                      <i data-feather="message-circle"></i>
                      Speaks: {doctor.doctorDetails.languages.join(', ')}
                    </p>
                  )}
                </div>
                {doctor.doctorDetails.bio && (
                  <p className="bio">{doctor.doctorDetails.bio}</p>
                )}
                <div className="doctor-actions">
                  <p className="consultation-fee">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }}>
                      <text x="12" y="18" textAnchor="middle" fontSize="16" fill="currentColor" fontFamily="Arial, sans-serif" fontWeight="bold">৳</text>
                    </svg>
                    ৳{Number(doctor.doctorDetails.consultationFee || 0).toLocaleString('en-BD')}
                  </p>
                  <button
                    className="btn btn-primary book-btn"
                    onClick={() => handleBookAppointment(doctor)}
                  >
                    <i data-feather="calendar"></i>
                    Book Appointment
                  </button>
                </div>
              </div>
            </div>
          ))}
          {!loading && doctors.length === 0 && (
            <div className="no-results">
              <i data-feather="search"></i>
              <p>No doctors found matching your search criteria</p>
              <small>Try adjusting your search terms or specialization</small>
            </div>
          )}
        </div>
      )}

      {showAvailabilityModal && selectedDoctor && (
        <div className="availability-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Dr. {selectedDoctor.name}'s Availability</h3>
              <button className="close-btn" onClick={handleCloseModal}>
                <i data-feather="x"></i>
              </button>
            </div>

            <div className="date-picker">
              <label>Select Date:</label>
              <input
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            {loadingAvailability ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading availability...</p>
              </div>
            ) : error ? (
              <div className="error-message">{error}</div>
            ) : availability ? (
              <>
                <div className="availability-info">
                  <p>Available slots for {new Date(availability.date).toLocaleDateString()}</p>
                  <p className="consultation-fee">
                    Consultation Fee: ৳{availability.doctor.consultationFee}
                  </p>
                </div>

                <div className="time-slots">
                  {availability.availableSlots.length > 0 ? (() => {
                    // Filter out past time slots
                    const validSlots = availability.availableSlots.filter(slot => !isSlotInPast(slot, selectedDate));
                    
                    if (validSlots.length === 0) {
                      return (
                        <div className="no-slots">
                          <p>No available slots for this date (all slots have passed)</p>
                        </div>
                      );
                    }
                    
                    return validSlots.map((slot, index) => (
                      <div key={index} className="time-slot available">
                        <span className="time">{slot.startTime} - {slot.endTime}</span>
                        <button 
                          className="btn btn-primary select-slot"
                          onClick={() => handleSelectTimeSlot(slot)}
                        >
                          Select
                        </button>
                      </div>
                    ));
                  })() : (
                    <div className="no-slots">
                      <p>No available slots for this date</p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="no-availability">
                <p>Please select a date to view availability</p>
              </div>
            )}
          </div>
        </div>
      )}

      {showBookingModal && selectedSlot && (
        <div className="booking-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Book Appointment</h3>
              <button className="close-btn" onClick={handleCloseModal}>
                <i data-feather="x"></i>
              </button>
            </div>

            <div className="booking-details">
              <p><strong>Doctor:</strong> Dr. {selectedDoctor.name}</p>
              <p><strong>Date:</strong> {new Date(selectedDate).toLocaleDateString()}</p>
              <p><strong>Time:</strong> {selectedSlot.startTime} - {selectedSlot.endTime}</p>
              <p><strong>Fee:</strong> ৳{selectedDoctor.doctorDetails.consultationFee}</p>
            </div>

            <div className="appointment-type">
              <label>Appointment Type:</label>
              <select
                value={appointmentType}
                onChange={(e) => setAppointmentType(e.target.value)}
              >
                <option value="in-person">In-Person</option>
                <option value="tele-consult">Tele-Consultation</option>
              </select>
            </div>

            <div className="booking-actions">
              <button className="btn btn-secondary" onClick={handleCloseModal}>
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleBookSlot}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Proceed to Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Gateway Modal */}
      {showPaymentGateway && selectedDoctor && selectedSlot && (
        <PaymentGateway
          open={showPaymentGateway}
          onClose={handlePaymentCancel}
          amount={selectedDoctor.doctorDetails.consultationFee}
          paymentType="consultation"
          onSuccess={handlePaymentSuccess}
          userProfile={userProfile}
          orderId={`APPT-${selectedDoctor._id}-${Date.now()}`}
          metadata={{
            doctorId: selectedDoctor._id,
            doctorName: selectedDoctor.name,
            date: selectedDate,
            time: `${selectedSlot.startTime} - ${selectedSlot.endTime}`,
            appointmentType: appointmentType
          }}
        />
      )}

    </div>
  );
};

export default DoctorSearch; 